/**
 * How cubic's agents name the tools of the MCP servers an org added, in PR
 * chat and in /code's OpenCode: `mcp-<server>_<tool>`. Server names hold no
 * `_` (`MCP_SERVER_NAME_PATTERN`), so the first one ends the server, and no
 * built-in tool starts with `mcp-`. Nothing here loads an MCP client, so
 * routes and the /code transcript can read these names.
 */

import { MCP_SERVER_NAME_SOURCE } from './types';

import { getNonEmptyString, getRecord } from '@/lib/utils/unknown';

export const MCP_TOOL_NAME_PREFIX = 'mcp-';

/**
 * OpenCode 2.0.26's Code Mode tool: the one direct tool a Code Mode server's
 * tools are called through, as `tools["mcp-<server>"].<tool>(…)` in its
 * `code`. Also the permission action that must be allowed for it to exist.
 */
export const OPENCODE_CODE_MODE_TOOL_NAME = 'execute';

/** An MCP tool as a step's label names it: the server's name, and the tool's. */
export type McpToolRef = { server: string; tool: string };

/** `mcp-<server>`: a server's OpenCode key and PR chat area, and the start of its tools' names. */
export function mcpServerKey(serverName: string): `mcp-${string}` {
  return `${MCP_TOOL_NAME_PREFIX}${serverName}`;
}

/**
 * `mcp-<server>_<tool>`, with the tool part limited to the characters every
 * model provider accepts. OpenCode names a tool, and its permission action,
 * the same way.
 */
export function mcpToolName(serverName: string, toolName: string): string {
  return `${mcpServerKey(serverName)}_${toolName.replace(/[^\w-]/g, '_')}`;
}

export function isMcpToolName(name: string): boolean {
  return name.startsWith(MCP_TOOL_NAME_PREFIX);
}

const MCP_TOOL_NAME = new RegExp(`^${MCP_TOOL_NAME_PREFIX}(${MCP_SERVER_NAME_SOURCE})_(.+)$`);

/** The server and tool an MCP tool name refers to, or null for any other tool. */
export function parseMcpToolName(toolName: string): McpToolRef | null {
  const match = MCP_TOOL_NAME.exec(toolName);
  return match ? { server: match[1]!, tool: match[2]! } : null;
}

// OpenCode's Code Mode records each tool a script called as
// `mcp-<server>.<tool>` in the `execute` call's metadata.
const MCP_CODE_MODE_TOOL_CALL = new RegExp(
  `^${MCP_TOOL_NAME_PREFIX}(${MCP_SERVER_NAME_SOURCE})\\.(.+)$`,
);

/** The server and tool of one recorded Code Mode call, or null for scripts and HTTP fetches. */
export function parseMcpCodeModeToolCall(call: string): McpToolRef | null {
  const match = MCP_CODE_MODE_TOOL_CALL.exec(call);
  return match ? { server: match[1]!, tool: match[2]! } : null;
}

/**
 * The MCP tools one tool call used, or null when it is not an MCP tool: a flat
 * `mcp-<server>_<tool>` name means itself; OpenCode 2.0.26's Code Mode `execute`
 * means the tools its metadata records (`metadata.toolCalls[].tool`,
 * `mcp-<server>.<tool>`), deduped in first-seen order and empty before any
 * lands. Other tools name no MCP tool.
 */
export function mcpCallsOf(toolName: string, metadata?: unknown): McpToolRef[] | null {
  const flat = parseMcpToolName(toolName);
  if (flat !== null) {
    return [flat];
  }
  if (toolName !== OPENCODE_CODE_MODE_TOOL_NAME) {
    return null;
  }
  const toolCalls = getRecord(metadata)?.toolCalls;
  if (!Array.isArray(toolCalls)) {
    return [];
  }
  const calls: McpToolRef[] = [];
  const seen = new Set<string>();
  for (const entry of toolCalls) {
    const call = parseMcpCodeModeToolCall(getNonEmptyString(getRecord(entry)?.tool) ?? '');
    if (call !== null && !seen.has(`${call.server}\u0000${call.tool}`)) {
      seen.add(`${call.server}\u0000${call.tool}`);
      calls.push(call);
    }
  }
  return calls;
}
